import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  Clock,
  Search,
  Tag,
  X,
} from 'lucide-react';
import { MasterRibbon } from '../../../components/MasterRibbon';
import { desktopRibbonInputClass } from '../../../components/ribbon/DesktopRibbonControls';
import { useMinhaMesaRibbon } from '../hooks/useMinhaMesaRibbon';
import type { QueueSortMode } from '../hooks/useMinhaMesaRibbon';
import { useUIStore } from '../../../store/useUIStore';
import type { WorkFilters, PeriodFilter, SortMode } from '../hooks/useWorkFilters';
import type { CardDensity } from './WorkKanbanView';
import type { WorkItemSource } from '../types/workItem';
import type { TaskState } from '../engine/taskStateMachine';

export type WorkViewMode = 'list' | 'kanban' | 'timeline';

type DropdownName = 'period' | 'type' | 'sort' | null;

const dropdownPanelClass =
  'ribbon-dropdown-panel enterprise-font fixed z-[999999] rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] p-1 text-[var(--text-primary)]';
const dropdownButtonClass =
  'flex h-8 w-full items-center gap-2 rounded-sm border border-transparent px-2 text-left text-[11px] font-medium text-[var(--text-primary)] hover:bg-[var(--button-hover)]';

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'this_year', label: 'Este ano' },
];

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: 'fifo', label: 'FIFO' },
  { value: 'sla_asc', label: 'SLA' },
  { value: 'lead_desc', label: 'Lead Time' },
  { value: 'idle_desc', label: 'Tempo parado' },
  { value: 'priority', label: 'Prioridade' },
];

const TYPE_OPTIONS: { value: WorkItemSource | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'action', label: 'Ação' },
  { value: 'problem', label: 'Problema' },
  { value: 'workflow', label: 'Workflow' },
];

export interface MinhaMesaRibbonProps {
  searchValue: string;
  workViewMode: WorkViewMode;
  filters: WorkFilters;
  hasActiveFilters: boolean;
  density: CardDensity;
  focusMode: boolean;
  cockpitActive: boolean;
  taskState?: TaskState;
  canComplete?: boolean;
  onFiltersChange: (updater: WorkFilters | ((prev: WorkFilters) => WorkFilters)) => void;
  onResetFilters: () => void;
  onDensityChange: () => void;
  onFocusModeToggle: () => void;
  onSearchChange: (value: string) => void;
  onSetWorkViewMode: (mode: WorkViewMode) => void;
  onClaimTask?: () => void;
  onStartTask?: () => void;
  onCompleteTask?: () => void;
  onPauseTask?: () => void;
  onResumeTask?: () => void;
  onRejectTask?: () => void;
  onTransferTask?: () => void;
  onEscalateTask?: () => void;
  onRegisterProblem?: () => void;
  onToggleHistory?: () => void;
  historyActive?: boolean;
  isCompleting?: boolean;
  isPausing?: boolean;
  isResuming?: boolean;
  isClaiming?: boolean;
  isStarting?: boolean;
  onPullNextTask?: () => void;
  canPullNext?: boolean;
  onExportProductivity?: () => void;
  queueSortMode?: QueueSortMode;
  onQueueSortModeChange?: (mode: QueueSortMode) => void;
}

function MinhaMesaRibbonImpl({
  searchValue,
  workViewMode,
  filters,
  hasActiveFilters,
  cockpitActive,
  onFiltersChange,
  onResetFilters,
  onSearchChange,
  onSetWorkViewMode,
  onToggleHistory,
  historyActive,
  onPullNextTask,
  canPullNext = false,
  onExportProductivity,
  queueSortMode = 'score',
  onQueueSortModeChange,
}: MinhaMesaRibbonProps) {
  const openLauncherDrawer = useUIStore((s) => s.openLauncherDrawer);
  const [openDropdown, setOpenDropdown] = useState<DropdownName>(null);
  const periodRef = useRef<HTMLDivElement>(null);
  const typeRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const [periodPos, setPeriodPos] = useState({ top: 0, left: 0 });
  const [typePos, setTypePos] = useState({ top: 0, left: 0 });
  const [sortPos, setSortPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const update = () => {
      if (openDropdown === 'period' && periodRef.current) {
        const r = periodRef.current.getBoundingClientRect();
        setPeriodPos({ top: r.bottom + 4, left: r.left });
      }
      if (openDropdown === 'type' && typeRef.current) {
        const r = typeRef.current.getBoundingClientRect();
        setTypePos({ top: r.bottom + 4, left: r.left });
      }
      if (openDropdown === 'sort' && sortRef.current) {
        const r = sortRef.current.getBoundingClientRect();
        setSortPos({ top: r.bottom + 4, left: r.left });
      }
    };
    const id = window.setTimeout(update, 0);
    if (openDropdown) {
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
    }
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-mm-dropdown="true"]')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  const currentTypeValue: WorkItemSource | 'all' =
    filters.sourceTypes.length === 1 ? filters.sourceTypes[0] : 'all';

  const currentPeriodLabel = PERIOD_OPTIONS.find(o => o.value === filters.period)?.label ?? 'Todos';
  const currentTypeLabel = TYPE_OPTIONS.find(o => o.value === currentTypeValue)?.label ?? 'Todos';
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === filters.sort)?.label ?? 'FIFO';

  // ── Filters widget (passed as afterGroups slot) ──────────────
  const filtersWidget = (
    <div className="flex items-center gap-1.5">
      {/* Reset */}
      {hasActiveFilters && (
        <button
          onClick={onResetFilters}
          className="flex h-[30px] items-center gap-0.5 rounded-sm px-1.5 text-[11px] font-medium text-red-500 hover:bg-[var(--button-hover)] transition-colors"
          title="Limpar filtros"
        >
          <X size={12} />
        </button>
      )}

      {/* Período */}
      <div ref={periodRef}>
        <button
          data-mm-dropdown="true"
          onClick={() => setOpenDropdown(openDropdown === 'period' ? null : 'period')}
          className="flex h-[30px] items-center gap-1.5 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer"
        >
          <Clock size={13} className="shrink-0 text-[var(--text-secondary)]" />
          <span className="truncate">{currentPeriodLabel}</span>
          <ChevronDown size={11} className="shrink-0 text-[var(--text-secondary)]" />
        </button>
      </div>
      {openDropdown === 'period' && createPortal(
        <div data-mm-dropdown="true" className={dropdownPanelClass} style={{ top: periodPos.top, left: periodPos.left, minWidth: 148 }}>
          {PERIOD_OPTIONS.map(o => (
            <button key={o.value} className={dropdownButtonClass} onClick={() => {
              onFiltersChange((prev) => ({ ...prev, period: o.value, customFrom: null, customTo: null }));
              setOpenDropdown(null);
            }}>
              <Check size={12} className={filters.period === o.value ? 'opacity-100' : 'opacity-0'} />
              {o.label}
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Tipo */}
      <div ref={typeRef}>
        <button
          data-mm-dropdown="true"
          onClick={() => setOpenDropdown(openDropdown === 'type' ? null : 'type')}
          className="flex h-[30px] items-center gap-1.5 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer"
        >
          <Tag size={13} className="shrink-0 text-[var(--text-secondary)]" />
          <span className="truncate">{currentTypeLabel}</span>
          <ChevronDown size={11} className="shrink-0 text-[var(--text-secondary)]" />
        </button>
      </div>
      {openDropdown === 'type' && createPortal(
        <div data-mm-dropdown="true" className={dropdownPanelClass} style={{ top: typePos.top, left: typePos.left, minWidth: 140 }}>
          {TYPE_OPTIONS.map(o => (
            <button key={o.value} className={dropdownButtonClass} onClick={() => {
              onFiltersChange((prev) => ({ ...prev, sourceTypes: o.value === 'all' ? [] : [o.value as WorkItemSource] }));
              setOpenDropdown(null);
            }}>
              <Check size={12} className={currentTypeValue === o.value ? 'opacity-100' : 'opacity-0'} />
              {o.label}
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Ordenar */}
      <div ref={sortRef}>
        <button
          data-mm-dropdown="true"
          onClick={() => setOpenDropdown(openDropdown === 'sort' ? null : 'sort')}
          className="flex h-[30px] items-center gap-1.5 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer"
        >
          <ArrowUpDown size={13} className="shrink-0 text-[var(--text-secondary)]" />
          <span className="truncate">{currentSortLabel}</span>
          <ChevronDown size={11} className="shrink-0 text-[var(--text-secondary)]" />
        </button>
      </div>
      {openDropdown === 'sort' && createPortal(
        <div data-mm-dropdown="true" className={dropdownPanelClass} style={{ top: sortPos.top, left: sortPos.left, minWidth: 148 }}>
          {SORT_OPTIONS.map(o => (
            <button key={o.value} className={dropdownButtonClass} onClick={() => {
              onFiltersChange((prev) => ({ ...prev, sort: o.value }));
              setOpenDropdown(null);
            }}>
              <Check size={12} className={filters.sort === o.value ? 'opacity-100' : 'opacity-0'} />
              {o.label}
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Busca */}
      {!cockpitActive && (
        <div className="flex w-[280px] shrink-0 items-center">
          <div className="flex w-full items-center gap-2 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
            <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar código, etapa ou descrição..."
              className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
            />
            {searchValue && (
              <button onClick={() => onSearchChange('')} className="shrink-0 p-0.5 rounded-sm hover:bg-[var(--button-hover)]">
                <X size={10} className="text-[var(--text-secondary)]" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const ribbonConfig = useMinhaMesaRibbon({
    canPullNext,
    cockpitActive,
    onPullNextTask,
    onOpenLauncher: openLauncherDrawer,
    workViewMode,
    onSetWorkViewMode,
    historyActive,
    onToggleHistory,
    onExportProductivity,
    queueSortMode,
    onQueueSortModeChange,
    filtersWidget,
  });

  return <MasterRibbon config={ribbonConfig} />;
}

// Prompt 34 — React.memo p/ estancar renderizações espúrias do Ribbon de filtros.
export const MinhaMesaRibbon = React.memo(MinhaMesaRibbonImpl);

